// PO7-S4 (docs/build-briefs/PO7-S4-invoice-confirm-and-cost.md). Pure helpers
// for the invoice review's confirm step and Confirmed panel: what each cost
// outcome says in plain words, the order's estimate beside the final invoice
// figure, who confirmed it and when, and whether the button works and, when it
// does not, why. No DOM, no network. Nothing here decides an outcome or a total:
// the server recorded them at the moment of confirming and this file only says
// them.

import type { InvoiceConfirmedLineDto, InvoiceReviewDto } from "./api.ts";
import { formatInvoiceDate } from "./invoiceAttachmentsDisplay.ts";
import { formatDraftMoney } from "./invoiceDraftDisplay.ts";
import { formatSignedMoney } from "./invoiceReviewDisplay.ts";

/** What happened to an item's cost when the invoice was confirmed. */
export function describeCostOutcome(
  line: Pick<
    InvoiceConfirmedLineDto,
    | "costOutcome"
    | "previousUnitCostCents"
    | "newUnitCostCents"
    | "unitCostCents"
    | "orderUnit"
    | "bottlesPerOrderUnit"
    | "priceReading"
    | "readUnitCostCents"
  >,
): string {
  switch (line.costOutcome) {
    case "posted": {
      const nowCents = line.newUnitCostCents ?? line.unitCostCents;
      const now = formatDraftMoney(nowCents);
      const pack = Number(line.bottlesPerOrderUnit ?? 0);
      if (pack > 1) {
        // PO7-S4-F4: a pack line moves per order unit, with the bottle figure beside it.
        const unit = line.orderUnit ?? "unit";
        const bottle =
          line.priceReading === "per_bottle" && line.readUnitCostCents !== null
            ? line.readUnitCostCents
            : Math.round(nowCents / pack);
        const to = `${now} a ${unit} (${formatDraftMoney(bottle)} a bottle)`;
        return line.previousUnitCostCents === null
          ? `Cost moved to ${to}.`
          : `Cost moved from ${formatDraftMoney(line.previousUnitCostCents)} a ${unit} to ${to}.`;
      }
      return line.previousUnitCostCents === null
        ? `Cost moved to ${now} a unit.`
        : `Cost moved from ${formatDraftMoney(line.previousUnitCostCents)} to ${now} a unit.`;
    }
    case "unchanged":
      return "Cost already matched what the vendor billed.";
    case "no_receipt":
      return "Nothing received yet, so there is no cost to move.";
    case "receipt_without_cost":
      return "The delivery was recorded without a cost, so there is no cost to move.";
    case "receipt_already_corrected":
      return "That delivery was already corrected, so its cost was left as it is.";
    case "conflicting_costs":
      return "This item is on the invoice at different costs, so its cost was left as it is.";
    case "cost_unresolved":
      return "The cost could not be worked out from the delivery, so it was left as it is.";
  }
}

/** The estimate and the final invoice figure side by side, with the signed
 * difference and, when some order lines had no cost, why the estimate is short. */
export function describeEstimatedVersusFinal(totals: {
  estimatedTotalCents: number;
  estimatedUnpricedLineCount: number;
  invoiceTotalCents: number;
  differenceCents: number;
}): { estimated: string; final: string; difference: string; note: string | null } {
  const unpriced = totals.estimatedUnpricedLineCount;
  return {
    estimated: formatDraftMoney(totals.estimatedTotalCents),
    final: formatDraftMoney(totals.invoiceTotalCents),
    difference: formatSignedMoney(totals.differenceCents),
    note:
      unpriced === 0
        ? null
        : unpriced === 1
          ? "1 order line had no cost, so the estimate is not the whole order."
          : `${unpriced} order lines had no cost, so the estimate is not the whole order.`,
  };
}

export function describeConfirmedBy(confirmed: {
  confirmedByName: string | null;
  confirmedAt: string;
}): string {
  const when = formatInvoiceDate(confirmed.confirmedAt);
  return confirmed.confirmedByName === null
    ? `Confirmed on ${when}.`
    : `Confirmed by ${confirmed.confirmedByName} on ${when}.`;
}

/** Whether Confirm invoice can be pressed and the line under it. The server's
 * reasons are the note whenever something blocks, whoever is looking; a session
 * without the permission is told so instead of being shown a button that cannot
 * work. */
export function confirmButtonState(input: {
  confirm: InvoiceReviewDto["confirm"];
  canConfirm: boolean;
  confirmed: boolean;
}): { enabled: boolean; note: string } {
  if (input.confirmed || !input.confirm.available) {
    return { enabled: false, note: input.confirm.reason };
  }
  if (!input.canConfirm) {
    return { enabled: false, note: "You do not have permission to confirm invoices." };
  }
  return { enabled: true, note: input.confirm.reason };
}
